import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, X, Ban, CalendarOff } from 'lucide-react';
import { unavailabilityRequestApi } from '../services/api';
import {
  UnavailabilityClashShift,
  UnavailabilityRequest,
  UpsertUnavailabilityRequest,
} from '../types';
import { useAuth } from '../context/AuthContext';
import { appConfirm } from '../context/SystemDialogContext';
import { SingleDayCalendarPicker } from '../components/SingleDayCalendarPicker';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { formatCompanyDate } from '../utils/companyDateFormat';

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

const emptyForm = (): UpsertUnavailabilityRequest => ({
  kind: 'OneOff',
  startDate: '',
  endDate: '',
  daysOfWeek: '',
  isAllDay: true,
  startTime: '09:00',
  endTime: '17:00',
  reason: '',
});

function selectedDays(daysOfWeek?: string | null): number[] {
  if (!daysOfWeek) return [];
  return daysOfWeek
    .split(',')
    .map((p) => Number(p.trim()))
    .filter((n) => n >= 0 && n <= 6);
}

function statusTone(status: string): string {
  if (status === 'Approved') return 'bg-slate-100 text-slate-800 border-slate-200';
  if (status === 'Denied' || status === 'Cancelled') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

const MyUnavailability: React.FC = () => {
  const { employee } = useAuth();
  const weekStartsOn = useWeekStartDay();
  const [rows, setRows] = useState<UnavailabilityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<UpsertUnavailabilityRequest>(emptyForm());
  const [formError, setFormError] = useState('');
  const [clashes, setClashes] = useState<UnavailabilityClashShift[]>([]);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    if (!employee?.id) return;
    try {
      const data = await unavailabilityRequestApi.getAll(employee.id);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const payload = useMemo<UpsertUnavailabilityRequest | null>(() => {
    if (!employee?.id || !form.startDate) return null;
    if (form.kind === 'OneOff' && !form.endDate) return null;
    if (form.kind === 'Weekly' && selectedDays(form.daysOfWeek).length === 0) return null;
    if (!form.isAllDay && (!form.startTime || !form.endTime)) return null;
    return {
      employeeId: employee.id,
      kind: form.kind,
      startDate: form.startDate,
      endDate: form.kind === 'Weekly' ? form.endDate || null : form.endDate || form.startDate,
      daysOfWeek: form.kind === 'Weekly' ? form.daysOfWeek : null,
      isAllDay: form.isAllDay,
      startTime: form.isAllDay ? null : form.startTime,
      endTime: form.isAllDay ? null : form.endTime,
      reason: form.reason?.trim() || null,
    };
  }, [employee?.id, form]);

  useEffect(() => {
    if (!payload || !showModal) {
      setClashes([]);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const handle = window.setTimeout(() => {
      unavailabilityRequestApi
        .checkClashes(payload)
        .then((found) => {
          if (!cancelled) setClashes(found);
        })
        .catch(() => {
          if (!cancelled) setClashes([]);
        })
        .finally(() => {
          if (!cancelled) setChecking(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [payload, showModal]);

  const toggleDay = (day: number) => {
    const current = selectedDays(form.daysOfWeek);
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setForm({ ...form, daysOfWeek: next.join(',') });
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm());
    setFormError('');
    setClashes([]);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payload) {
      setFormError('Fill in the required fields.');
      return;
    }
    if (clashes.length > 0) {
      setFormError('This overlaps an existing shift. Change the dates or times, or ask your manager to move the shift.');
      return;
    }
    try {
      setSubmitting(true);
      setFormError('');
      await unavailabilityRequestApi.create(payload);
      closeModal();
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; clashes?: UnavailabilityClashShift[] } } };
      if (ax.response?.data?.clashes?.length) {
        setClashes(ax.response.data.clashes);
      }
      setFormError(ax.response?.data?.message || 'Could not submit unavailability.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (row: UnavailabilityRequest) => {
    if (!(await appConfirm(`Cancel this unavailability (${row.summary})?`))) return;
    await unavailabilityRequestApi.cancel(row.id);
    await load();
  };

  const skipOnce = async (row: UnavailabilityRequest) => {
    const date = window.prompt('Skip which date? (yyyy-MM-dd)');
    if (!date) return;
    await unavailabilityRequestApi.skipDate(row.id, date);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">My Unavailability</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Mark days or hours you cannot be rostered. A manager must approve the request.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Mark unavailable
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 py-12">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-neutral-500">
          You have not marked any unavailability yet.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Pattern</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Approval</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">{row.summary}</div>
                    {row.reason && <div className="text-xs text-neutral-500 mt-0.5">{row.reason}</div>}
                    {row.skipDates && row.skipDates.length > 0 && (
                      <div className="text-xs text-neutral-400 mt-1">
                        Skipped: {row.skipDates.map((s) => formatCompanyDate(s.date)).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${statusTone(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {row.status === 'Approved' && row.approvedByName
                      ? `${row.approvedByName}${row.approvedAt ? ` · ${formatCompanyDate(row.approvedAt)}` : ''}`
                      : row.rejectionReason || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {(row.status === 'Pending' || row.status === 'Approved') && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => cancel(row)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded-md text-red-700 hover:bg-red-50"
                        >
                          <Ban className="h-3.5 w-3.5" />
                          Cancel
                        </button>
                        {row.kind === 'Weekly' && (
                          <button
                            type="button"
                            onClick={() => skipOnce(row)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded-md text-neutral-700 hover:bg-neutral-50"
                          >
                            <CalendarOff className="h-3.5 w-3.5" />
                            Skip a date
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Mark unavailable</h2>
              <button type="button" onClick={closeModal} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Pattern</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={form.kind === 'OneOff'}
                      onChange={() => setForm({ ...form, kind: 'OneOff' })}
                    />
                    One-off dates
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={form.kind === 'Weekly'}
                      onChange={() => setForm({ ...form, kind: 'Weekly' })}
                    />
                    Every week
                  </label>
                </div>
              </div>

              {form.kind === 'Weekly' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Weekdays</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((d) => (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleDay(d.value)}
                        className={`px-2.5 py-1 rounded-md text-sm border ${
                          selectedDays(form.daysOfWeek).includes(d.value)
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-neutral-700'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {form.kind === 'Weekly' ? 'From' : 'Start date'} *
                  </label>
                  <SingleDayCalendarPicker
                    value={form.startDate}
                    onChange={(ymd) => setForm({ ...form, startDate: ymd, endDate: form.endDate || ymd })}
                    weekStartsOn={weekStartsOn}
                    max={form.endDate || undefined}
                    displayFormat="dd/mm/yyyy"
                    className="block w-full"
                    buttonClassName="w-full justify-start rounded-md px-3 py-2"
                    aria-label={form.kind === 'Weekly' ? 'From date' : 'Start date'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {form.kind === 'Weekly' ? 'Until (optional)' : 'End date'}
                    {form.kind === 'OneOff' ? ' *' : ''}
                  </label>
                  <SingleDayCalendarPicker
                    value={form.endDate || ''}
                    onChange={(ymd) => setForm({ ...form, endDate: ymd })}
                    weekStartsOn={weekStartsOn}
                    min={form.startDate || undefined}
                    displayFormat="dd/mm/yyyy"
                    className="block w-full"
                    buttonClassName="w-full justify-start rounded-md px-3 py-2"
                    aria-label={form.kind === 'Weekly' ? 'Until date' : 'End date'}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isAllDay}
                  onChange={(e) => setForm({ ...form, isAllDay: e.target.checked })}
                />
                All day
              </label>

              {!form.isAllDay && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">From</label>
                    <input
                      type="time"
                      value={form.startTime || ''}
                      onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">To</label>
                    <input
                      type="time"
                      value={form.endTime || ''}
                      onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Reason</label>
                <textarea
                  value={form.reason || ''}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  rows={3}
                />
              </div>

              {checking && <p className="text-xs text-neutral-500">Checking existing shifts…</p>}
              {clashes.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-medium mb-1">This clashes with existing shifts. You cannot submit until they are moved.</p>
                  <ul className="space-y-1">
                    {clashes.map((c) => (
                      <li key={c.shiftId}>
                        {formatCompanyDate(c.date)} {c.startTime}–{c.endTime}
                        {c.scheduleName ? ` · ${c.scheduleName}` : ''}
                        {c.locationName ? ` · ${c.locationName}` : ''}
                        {' · '}
                        {c.publicationStatus || 'Unpublished'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {formError && <p className="text-sm text-red-600">{formError}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-md">
                  Close
                </button>
                <button
                  type="submit"
                  disabled={submitting || clashes.length > 0 || checking}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit for approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyUnavailability;
